import { useEffect, useState } from 'react';
import { 
  Sparkles, Clock, Zap, Database, TrendingUp, 
  ArrowUpRight, ArrowDownRight, Activity, 
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { 
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis 
} from 'recharts';
import { aiApi } from '@/api/ai';
import { cn } from '@/lib/utils';

function StatCard({ icon, label, value, sub, trend }: { 
  icon: React.ReactNode; 
  label: string; 
  value: string | number; 
  sub?: string;
  trend?: { val: string; up: boolean }
}) {
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-slate-100 p-5 flex flex-col gap-3 transition-all hover:shadow-md">
      <div className="flex items-center justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-600 border border-slate-100">
          {icon}
        </div>
        {trend && (
          <div className={cn("flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full", 
            trend.up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600")}>
            {trend.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {trend.val}
          </div>
        )}
      </div>
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="text-2xl font-black text-slate-800 tracking-tight mt-1">{value}</p>
        {sub && <p className="text-[10px] font-medium text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export default function AIMasterTab() {
  const [stats, setStats] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        aiApi.getStats(),
        aiApi.getUsageHistory(25)
      ]);
      setStats(s);
      setHistory(h);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('AI stats load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!stats && loading) return <div className="h-full flex items-center justify-center"><Loader /></div>;
  const activeModels = (stats?.models || []).filter((model: any) => model.is_active);
  const activeModelLabel = activeModels.length
    ? activeModels.map((model: any) => model.display_name || model.vendor).join(' / ')
    : 'Aktif model yok';
  const quotaPercent = stats?.limit ? Math.min(100, Math.round(((stats?.used || 0) / stats.limit) * 100)) : 0;

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-500">
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          icon={<Zap size={20} className="text-blue-500" />} 
          label="Toplam Token Kullanımı" 
          value={stats?.used?.toLocaleString() || '0'} 
          sub="Tüm zamanlar birikmiş"
          trend={{ val: "Canlı", up: true }}
        />
        <StatCard 
          icon={<Database size={20} className="text-purple-500" />} 
          label="Kalan Kota" 
          value={stats?.remaining?.toLocaleString() || '0'} 
          sub={`Limit: ${stats?.limit?.toLocaleString()}`}
        />
        <StatCard 
          icon={<Clock size={20} className="text-emerald-500" />} 
          label="Ortalama Gecikme" 
          value={`${((history.reduce((a, b) => a + b.latency_ms, 0) / (history.length || 1)) / 1000).toFixed(2)}s`} 
          sub="Son 25 istek ortalaması"
        />
        <StatCard 
          icon={<Sparkles size={20} className="text-amber-500" />} 
          label="Aktif Modeller" 
          value={activeModels.length.toString()} 
          sub={activeModelLabel}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Usage Chart */}
        <div className="lg:col-span-2 rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500"><TrendingUp size={20} /></div>
              <div>
                <h3 className="text-[14px] font-black text-slate-800 uppercase tracking-tight">Günlük Kullanım Trendi</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Son 7 Günlük Token Harcaması</p>
              </div>
            </div>
            <button onClick={loadData} className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 transition-all"><RefreshCw size={18} className={cn(loading && "animate-spin")} /></button>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.dailyTrend || []}>
                <defs>
                  <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 600, fill: '#94a3b8'}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 600, fill: '#94a3b8'}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', fontSize: '11px', fontWeight: 'bold' }}
                  labelStyle={{ color: '#64748b', marginBottom: '4px' }}
                />
                <Area type="monotone" dataKey="tokens" name="Token" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorTokens)" animationDuration={1500} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Info Column */}
        <div className="space-y-4">
          <div className="rounded-[24px] bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-xl relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all" />
            <Sparkles className="text-blue-400 mb-4" size={32} />
            <h4 className="text-lg font-black leading-tight mb-2">AI Kapasite Durumu</h4>
            <p className="text-slate-400 text-[11px] leading-relaxed mb-6 font-medium">Monitoring paneli artık AI servisindeki gerçek model konfigürasyonunu, kullanım kayıtlarını ve kalan kota durumunu canlı olarak gösteriyor.</p>
            <div className="space-y-3">
              <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-500 items-end">
                <span>KOTA DOLULUK</span>
                <span className="text-blue-400 text-sm">{quotaPercent}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-600 to-indigo-400 transition-all duration-1000" 
                  style={{ width: `${quotaPercent}%` }} 
                />
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
             <div className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4"><Activity size={14} className="text-emerald-500" /> Servis Durumu</div>
             <div className="space-y-3">
                {(stats?.models || []).map((model: any) => (
                  <div key={model.vendor} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <div className={cn("w-1.5 h-1.5 rounded-full", model.is_active ? "bg-emerald-500 animate-pulse" : "bg-slate-300")} />
                      <span className="text-[11px] font-bold text-slate-700">{model.display_name || model.vendor}</span>
                    </div>
                    <span className={cn("text-[9px] font-black px-2 py-0.5 rounded-full", model.is_active ? "text-emerald-600 bg-emerald-50" : "text-slate-500 bg-slate-100")}>
                      {model.is_active ? 'AKTİF' : 'PASİF'}
                    </span>
                  </div>
                ))}
             </div>
          </div>
          <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4"><BarChart3 size={14} className="text-blue-500" /> Son 7 Gün Vendor Kullanımı</div>
            <div className="space-y-3">
              {(stats?.vendorUsage || []).length ? (stats.vendorUsage || []).map((row: any) => (
                <div key={row.vendor} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-black text-slate-700 uppercase">{row.vendor}</span>
                    <span className="text-[10px] font-bold text-slate-400">{Number(row.request_count || 0).toLocaleString()} istek</span>
                  </div>
                  <div className="mt-1 text-[12px] font-black text-blue-600">{Number(row.total_tokens || 0).toLocaleString()} token</div>
                </div>
              )) : (
                <div className="rounded-xl bg-slate-50 p-3 text-[11px] font-medium text-slate-500">Henüz kullanım verisi oluşmamış.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Usage Logs Table */}
      <div className="rounded-[24px] border border-slate-100 bg-white shadow-sm overflow-hidden mb-12">
        <div className="p-6 border-b border-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
               <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400"><BarChart3 size={20} /></div>
               <div>
                  <h3 className="text-[14px] font-black text-slate-800 uppercase tracking-tight">Kullanım Günlüğü</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-xs">Son 25 İstek Detayı</p>
               </div>
            </div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Son Güncelleme: {lastRefresh.toLocaleTimeString()}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">ZAMAN</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">SAĞLAYICI / MODEL</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">GİRDİ</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">ÇIKTI</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">TOPLAM</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">GECİKME</th>
              </tr>
            </thead>
            <tbody>
              {history.map((log) => (
                <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4 text-[11px] font-bold text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString('tr-TR')}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                       <span className="text-[12px] font-black text-slate-800 uppercase">{log.vendor}</span>
                       <span className="text-[9px] font-bold text-slate-400 truncate max-w-[150px]">{log.model}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[12px] font-bold text-slate-600">{log.prompt_tokens?.toLocaleString()}</td>
                  <td className="px-6 py-4 text-[12px] font-bold text-slate-600">{log.candidates_tokens?.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-black">
                      {log.total_tokens?.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                       <div className={cn("w-1.5 h-1.5 rounded-full", log.latency_ms > 5000 ? "bg-rose-500" : log.latency_ms > 2000 ? "bg-amber-500" : "bg-emerald-500")} />
                       <span className="text-[12px] font-bold text-slate-600">{(log.latency_ms / 1000).toFixed(2)}s</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Loader() {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 animate-bounce">
        <Sparkles size={24} />
      </div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">Analitik Veriler Toplanıyor...</p>
    </div>
  );
}
